// src/components/dialog/DlgConsumptionTableCols.tsx

import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useState, useEffect } from 'react'

import { UserId } from '@/types/domain'
import { FolderId } from '@/types/domain/folder'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import {
  OperatorData,
  useOperator,
  useOperatorApi,
  useRoomsMode,
} from '../../hooks'

type Props = KARMA_BasicDialog & {
  id: FolderId
  onConfirm: (_cols: any, _unitConfig: any) => void
}

export const DlgConsumptionTableCols = ({
  id,
  onClose,
  onConfirm,
  open,
}: Props) => {
  const roomsMode = useRoomsMode(id)
  const [userId, setUserId] = useState<UserId>(null)

  const { update: updateOperator } = useOperatorApi()

  const opData: OperatorData = useOperator()

  const data = [
    { label: 'Datum', accessor: 'date' },
    { label: 'Verbrauch', accessor: 'actual_consumption' },
    { label: 'Trend', accessor: 'trend' },
    { label: 'Zählerstand', accessor: 'counter_reading' },
    { label: 'CO₂-Emissionen', accessor: 'emissions' },
    { label: 'Verbrauch / m²', accessor: 'area_consumption' },
    { label: `Verbrauch / ${roomsMode}`, accessor: 'employee_consumption' },
    { label: 'Kosten', accessor: 'actual_costs' },
    { label: 'Kosten / m²', accessor: 'area_costs' },
    { label: `Kosten / ${roomsMode}`, accessor: 'employee_costs' },
    { label: 'Benchmark', accessor: 'benchmark' },
  ]

  const [useGlobalVO, setUseGlobalVO] = useState(null)

  const [cols, setCols] = useState({
    date: true,
    actual_consumption: true,
    trend: true,
    counter_reading: true,
    emissions: true,
    area_consumption: true,
    employee_consumption: true,
    actual_costs: true,
    area_costs: true,
    employee_costs: true,
    benchmark: true,
  })

  const [unitConfig, setUnitConfig] = useState({
    header: false,
    values: true,
  })

  useEffect(() => {
    if (opData === null) return

    // operatorId umbenennen, damit der Folder-Prop "id" nicht überschattet wird
    const { id: operatorId, view_options } = opData

    const data = [
      { label: 'Datum', accessor: 'date' },
      { label: 'Verbrauch', accessor: 'actual_consumption' },
      { label: 'Trend', accessor: 'trend' },
      { label: 'Zählerstand', accessor: 'counter_reading' },
      { label: 'CO₂-Emissionen', accessor: 'emissions' },
      { label: 'Verbrauch / m²', accessor: 'area_consumption' },
      { label: `Verbrauch / ${roomsMode}`, accessor: 'employee_consumption' },
      { label: 'Kosten', accessor: 'actual_costs' },
      { label: 'Kosten / m²', accessor: 'area_costs' },
      { label: `Kosten / ${roomsMode}`, accessor: 'employee_costs' },
      { label: 'Benchmark', accessor: 'benchmark' },
    ]

    // do we have view options for all folders? (view options but no fId in record)
    const voAllFolders = view_options.find(f => f.folder_id === 0)

    // do we have view options for the current folder? (view options with fId matching the fId in record)
    // leere columns ("") = Sentinel: "gilt global", Record existiert aber ohne Einstellung
    const voCurrentFolder = view_options.find(
      f => f.folder_id === id && f.columns
    )

    if (voAllFolders) setUseGlobalVO(true)
    // folder-spezifischer Record mit Inhalt hat Vorrang vor dem globalen
    if (voCurrentFolder) setUseGlobalVO(false)

    const cols = {
      date: true,
      actual_consumption: true,
      trend: true,
      counter_reading: true,
      emissions: true,
      area_consumption: true,
      employee_consumption: true,
      actual_costs: true,
      area_costs: true,
      employee_costs: true,
      benchmark: true,
    }

    if (voAllFolders) {
      const vo = voAllFolders.columns
        .split('x')[1]
        .split('')
        .map(f => Boolean(Number(f)))

      data.forEach((col, key) => {
        cols[col.accessor] = vo[key]
      })

      setCols(cols)

      const uc = voAllFolders.columns
        .split('x')[0]
        .split('')
        .map(f => Boolean(Number(f)))
      setUnitConfig({ header: uc[0], values: uc[1] })
    }

    if (voCurrentFolder) {
      const vo = voCurrentFolder.columns
        .split('x')[1]
        .split('')
        .map(f => Boolean(Number(f)))

      data.forEach((col, key) => {
        cols[col.accessor] = vo[key]
      })

      setCols(cols)

      const uc = voCurrentFolder.columns
        .split('x')[0]
        .split('')
        .map(f => Boolean(Number(f)))
      setUnitConfig({ header: uc[0], values: uc[1] })
    }

    setUserId(operatorId)
  }, [opData, id, roomsMode])

  const onUpdate = () => {
    const { view_options } = opData

    const folder_id = useGlobalVO === true ? 0 : id
    const uCfg = `${Number(unitConfig.header)}${Number(unitConfig.values)}`

    const columns =
      uCfg +
      'x' +
      Object.values(cols)
        .map(item => Number(Boolean(item)))
        .toString()
        .replaceAll(',', '')

    const allOptions = view_options || []
    const exists = allOptions.some(v => v.folder_id === folder_id)

    // Wenn "alle Gebäude" gespeichert wird, folder-spezifischen Record auf ""
    // setzen (Sentinel). Das Backend merged statt zu löschen, daher können wir
    // den Record nicht entfernen — leere columns signalisieren "kein Override".
    const withSentinel = useGlobalVO
      ? allOptions.map(v =>
          v.folder_id === id && v.folder_id !== 0 ? { ...v, columns: '' } : v
        )
      : allOptions

    const vOptions = exists
      ? withSentinel.map(v =>
          v.folder_id === folder_id ? { ...v, columns } : v
        )
      : [
          ...withSentinel,
          { entity_id: null, user_id: userId, folder_id, columns },
        ]

    const updatedData = { ...opData, view_options: vOptions }

    updateOperator(userId, updatedData)
    onConfirm(cols, unitConfig)
    onClose()
  }

  const onToggleHeader = () =>
    setUnitConfig(prev => ({ ...prev, header: !prev.header }))

  const onToggleValues = () =>
    setUnitConfig(prev => ({ ...prev, values: !prev.values }))

  return (
    <Dialog maxWidth={'lg'} open={open} onClose={onClose}>
      <DialogTitle>Tabellenansicht</DialogTitle>

      <DialogContent>
        <Alert severity={'info'} sx={{ mt: 2 }}>
          <AlertTitle>Darstellen von Einheiten</AlertTitle>

          <Typography>
            Einheiten können im Spaltennamen und/oder hinter Werten angezeigt
            werden.
          </Typography>
        </Alert>

        <FormControl sx={{ m: 2, display: 'block' }}>
          <List>
            <ListItemButton onClick={onToggleHeader} sx={{ p: 0, width: 1 }}>
              <ListItemText primary={'Einheit im Spaltennamen anzeigen'} />

              <Checkbox checked={unitConfig.header === true} />
            </ListItemButton>

            <ListItemButton onClick={onToggleValues} sx={{ p: 0 }}>
              <ListItemText primary={'Einheit hinter Werten anzeigen'} />

              <Checkbox checked={unitConfig.values === true} />
            </ListItemButton>
          </List>
        </FormControl>

        <Alert severity={'info'} sx={{ mt: 2 }}>
          <AlertTitle>Spaltenkonfiguration</AlertTitle>

          <Typography>
            Bestimmen Sie hier, welche Spalten in der Tabelle angezeigt werden
            sollen.
          </Typography>

          <Typography>
            Die Spalte Datum ist erforderlich und lässt sich nicht deaktivieren.
          </Typography>
        </Alert>

        <List>
          {data.map((cell, key) => {
            const { accessor, label } = cell
            const checked = cols[accessor]

            const toggle = () =>
              setCols(prev => {
                const newVO = { ...prev }
                newVO[accessor] = !prev[accessor]

                return newVO
              })

            return (
              <ListItem key={label} sx={{ p: 0 }}>
                <ListItemButton
                  disabled={key === 0}
                  onClick={toggle}
                  sx={{ py: 0 }}
                >
                  <ListItemText>{label}</ListItemText>

                  <Checkbox checked={checked} disabled={key === 0} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>

        <Collapse in={true}>
          <Divider />

          <Alert severity={'info'}>
            <AlertTitle>Gültigkeit</AlertTitle>

            <Typography>
              Wenn Sie über mehrere Gebäude verfügen, können Sie die Gültigkeit
              der Spaltenkonfiguration reglementieren.
            </Typography>
          </Alert>

          <FormControl sx={{ m: 2 }}>
            <FormLabel>Anwenden auf</FormLabel>

            <RadioGroup value={useGlobalVO}>
              {[
                {
                  fn: () => setUseGlobalVO(false),
                  label: 'aktuelles Gebäude',
                  value: false,
                },
                {
                  fn: () => setUseGlobalVO(true),
                  label: 'alle meine Gebäude',
                  value: true,
                },
              ].map(item => (
                <FormControlLabel
                  control={<Radio />}
                  key={String(item.value)}
                  label={item.label}
                  onClick={item.fn}
                  value={item.value}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button disabled={useGlobalVO === null} onClick={onUpdate}>
          Speichern
        </Button>

        <Button onClick={onClose}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  )
}
